import { CommonModule, formatDate } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { SessionStorageService } from '../../services/session-storage.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { CrossDockOrchestration } from '../transport-planning.model';
import { TransportPlanningService } from '../transport-planning.service';

@Component({
  selector: 'app-transport-cross-dock-orchestration-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    TableModule,
    TagModule,
    PageShellComponent,
    PageHeroComponent,
    PageDataPanelComponent
  ],
  templateUrl: './transport-cross-dock-orchestration-list.component.html',
  styleUrls: ['./transport-cross-dock-orchestration-list.component.css']
})
export class TransportCrossDockOrchestrationListComponent implements OnInit {
  rows: CrossDockOrchestration[] = [];
  isLoading = false;
  message = '';
  keyword = '';
  status: string | null = null;

  readonly statusOptions = [
    { label: 'Planned', value: 'PLANNED' },
    { label: 'In Progress', value: 'IN_PROGRESS' },
    { label: 'Completed', value: 'COMPLETED' },
    { label: 'Migrated Partial', value: 'MIGRATED_PARTIAL' }
  ];

  constructor(
    private service: TransportPlanningService,
    private session: SessionStorageService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    const companyId = this.companyId();
    if (!companyId) {
      this.message = 'Choose a company first.';
      return;
    }
    this.isLoading = true;
    this.service.getCrossDockOrchestrations(companyId, this.status).subscribe({
      next: rows => {
        this.rows = rows ?? [];
        this.isLoading = false;
      },
      error: () => {
        this.message = 'Could not load cross-dock orchestrations.';
        this.isLoading = false;
      }
    });
  }

  filteredRows(): CrossDockOrchestration[] {
    const keyword = this.keyword.trim().toLowerCase();
    if (!keyword) return this.rows;
    return this.rows.filter(row => [
      row.crossDockOrchestrationNumber,
      row.sourceType,
      row.sourceNumber,
      row.sourceId,
      row.status,
      row.validationMessage,
      row.parentReleaseId
    ].some(value => String(value ?? '').toLowerCase().includes(keyword)));
  }

  open(row: CrossDockOrchestration): void {
    if (!row.crossDockOrchestrationId) return;
    this.router.navigate(['/transport-planning/orchestrations', row.crossDockOrchestrationId]);
  }

  date(value?: string | Date): string {
    return value ? formatDate(value, 'dd MMM yyyy HH:mm', 'en-US') : '-';
  }

  severity(status?: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
    const value = (status || '').toUpperCase();
    if (value.includes('COMPLETE')) return 'success';
    if (value.includes('BLOCK') || value.includes('FAIL') || value.includes('ERROR')) return 'danger';
    if (value.includes('PROGRESS')) return 'info';
    if (value.includes('MIGRATED')) return 'warn';
    return 'secondary';
  }

  private companyId(): number {
    return this.session.getCompany()?.id ?? 0;
  }
}
